import { createI18n } from 'vue-i18n';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@/i18n/locales';
import ru from '@/i18n/locales/ru';
import en from '@/i18n/locales/en';
import type { MessageSchema } from '@/i18n/types';

/**
 * Определяет стартовую локаль:
 *   1. localStorage `bafsy.locale`, если валиден;
 *   2. `navigator.language` — `en*` → `en`, иначе `ru`;
 *   3. Фолбэк — `DEFAULT_LOCALE` (ru).
 *
 * SSR-safe: в serverside-пустой среде вернёт `DEFAULT_LOCALE`.
 */
export function detectLocale(): Locale {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem('bafsy.locale');
      if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
        return stored as Locale;
      }
    } catch {
      // localStorage может быть заблокирован (private mode / SecurityError) —
      // не мешаем, продолжаем по другим сигналам.
    }
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      const lang = navigator.language.toLowerCase();
      if (lang.startsWith('en')) return 'en';
      if (lang.startsWith('ru')) return 'ru';
    }
  }
  return DEFAULT_LOCALE;
}

export const i18n = createI18n<{ message: MessageSchema }, Locale, false>({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  availableLocales: [...SUPPORTED_LOCALES],
  messages: {
    ru,
    en,
  },
  missingWarn: true,
  fallbackWarn: false,
  silentTranslationWarn: false,
});

export type I18nInstance = typeof i18n;
